/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Icon,
  InlineMessage,
  Link,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import config from '../../../../config/obNextGenConfig';
import testIds from '../../../../constants/testIds';
import useLocalization from '../../../../hooks/use-localize';
import { type RootState } from '../../../../redux/obNextGenStore';
import Logger from '../../../../services/loggerService';
import DiscoveryImport from '../../../DiscoveryImport/components/DiscoveryImport';
import { type DiscoveryExistingConnectionState } from '../../data-layer/DiscoveryExistingConnectionSlice';

import './ApplicationSuccess.css';

interface ApplicationSuccessProps {
  useExisting: boolean;
  openAsModal: boolean;
  resetSuccess?: (state: boolean) => void;
  sourceAppId: string | number;
  appTypeKey: number;
  image: string | undefined;
  iobAppKey: number;
  skip?: () => void;
  resetUseExisting?: (value: boolean) => void;
}

/**
 * Application Success Component
 */
function ApplicationSuccess({
  useExisting,
  openAsModal,
  resetSuccess = () => {},
  sourceAppId,
  skip,
  appTypeKey,
  image,
  iobAppKey,
  resetUseExisting,
}: ApplicationSuccessProps) {
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(openAsModal);
  const [importFlow, setImportFlow] = useState(false);
  const sectionRef = useRef(null);
  const formatMessage = useLocalization();
  const discoveryExisitngConnectionSaveStatus = useSelector(
    (state: RootState): DiscoveryExistingConnectionState =>
      state.discoveryExistingConnection as DiscoveryExistingConnectionState
  );
  const testFailedValue = useExisting
    ? discoveryExisitngConnectionSaveStatus.data?.testConnectionStatus !==
      'CONNECTED'
    : false;

  const handleCloseModal = () => {
    resetUseExisting?.(false);
    setIsModalOpen(false);
    resetSuccess(false);
  };

  Logger.log('sourceAppId', sourceAppId);

  const handleImport = () => {
    setImportFlow(true);
  };

  const handleBackToConfigPage = useCallback(() => {
    skip?.();

    if (discoveryExisitngConnectionSaveStatus.data?.iobAppId) {
      history.push(
        `${config.URL_OB_NEXT_GEN_DISCOVERY_CONFIG}?id=${discoveryExisitngConnectionSaveStatus.data.iobAppId}`
      );
    }
  }, [discoveryExisitngConnectionSaveStatus.data?.iobAppId, history, skip]);

  const handleApplicationSuccesContent = () => {
    if (importFlow) {
      return (
        <DiscoveryImport
          appTypeKey={appTypeKey}
          iconLink={image ?? ''}
          iobAppKey={iobAppKey}
          closeStartImportModal={skip}
        />
      );
    }

    return (
      <div className='ApplicationSucess-Container'>
        <Box>
          <Icon
            className='ApplicationSucess-Container-Icon'
            kind='CheckCircleFilledSeparateColors'
            color='--color-foreground-success-subtle'
            size='xxLarge'
          />
        </Box>
        <Box className='ApplicationSucess-Container-Content'>
          <Typography kind='h1'>
            {formatMessage(
              'INTEGRATIONS.DISCOVERY.SERVICENOW.SAVED',
              'ServiceNow Saved!'
            )}
          </Typography>
          {!isModalOpen && testFailedValue ? (
            <Typography
              className='ApplicationSucess-Container-Content-Failure'
              color='--color-foreground-neutral-subtle'
              kind='body1'>
              {formatMessage(
                'INTEGRATIONS.DISCOVERY.EXISTING.CONNECTION.FAILED',
                'Your discovery source has been successfully created, but the connection to ServiceNow has failed. To start importing assets, please update the connection details on the discovery source configuration page '
              )}
              <Link
                text={formatMessage('INTEGRATIONS.DISCOVERY.HERE', 'here')}
                kind='button'
                dataTestId={
                  testIds.DISCOVERY_APPLICATIONCARD_SUCCESS_IMPORT_SKIP_LINK
                }
                onClick={handleBackToConfigPage}
              />
            </Typography>
          ) : (
            <>
              <Typography
                color='--color-foreground-neutral-subtle'
                kind='body1'>
                {formatMessage(
                  'INTEGRATIONS.DISCOVERY.SERVICENOW.SAVED.DESCRIPTION',
                  'Your connection to the discovery source ServiceNow has been successfully tested and saved.'
                )}
              </Typography>
              <InlineMessage
                text={formatMessage(
                  'INTEGRATIONS.DISCOVERY.SERVICENOW.SAVED.INLINE',
                  'Once the import starts, it cannot be canceled'
                )}
                colorTheme='info'
                size='medium'
                leftIcon={<Icon kind='AlertInfoFilled' />}
              />
            </>
          )}
        </Box>
        {!isModalOpen && testFailedValue ? null : (
          <Box className='ApplicationSucess-Container-Actions'>
            <Button
              onClick={handleImport}
              type='button'
              kind='filled'
              dataTestId={
                testIds.DISCOVERY_APPLICATIONCARD_SUCCESS_IMPORT_BUTTON
              }
              size='large'>
              {formatMessage('INTEGRATIONS.DISCOVERY.IMPORT', 'Import Assets')}
            </Button>
            <Link
              text={formatMessage(
                'INTEGRATIONS.DISCOVERY.SKIP_FOR_NOW',
                'Skip For Now'
              )}
              kind='button'
              dataTestId={
                testIds.DISCOVERY_APPLICATIONCARD_SUCCESS_IMPORT_SKIP_LINK
              }
              onClick={skip}
            />
          </Box>
        )}
      </div>
    );
  };

  const handleModalContent = () => {
    if (importFlow) {
      return (
        <DiscoveryImport
          appTypeKey={appTypeKey}
          iconLink={image ?? ''}
          iobAppKey={iobAppKey}
          closeStartImportModal={skip}
        />
      );
    }

    return handleApplicationSuccesContent();
  };

  return (
    <section className='ApplicationSucess'>
      {openAsModal ? (
        <ModalIsland
          kind='default'
          onClose={handleCloseModal}
          isOpen={isModalOpen}
          title='Discovery'
          headerIcon={
            <Icon kind='ManageOverview' size='large' aria-hidden='true' />
          }
          contentBody={handleModalContent()}
          sectionRef={sectionRef}
        />
      ) : (
        handleApplicationSuccesContent()
      )}
    </section>
  );
}

ApplicationSuccess.defaultProps = {
  resetSuccess: () => {},
  skip: () => {},
  resetUseExisting: () => {},
};

export default ApplicationSuccess;
